import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
	Skeleton,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Box,
	Tab,
	TableContainer,
	Grid,
	Alert,
	useTheme,
} from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import {
	CerebroBreadcrumb,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroHeading,
	CerebroLink,
	CerebroPageContainer,
	CerebroSecondaryButton,
	CerebroText,
} from '../../shared/components';
import { MatchingGrid } from './MatchingGrid';
import {
	QueryKeys,
	useDealQuery,
	useDealRequestAllocationQuery,
	useIsMatchingProcessingQuery,
	useMatchingQuery,
} from '../../shared/hooks/Queries';
import { useMatchingTrancheLendersQuery } from '../../shared/hooks/Queries/useMatchingTrancheLendersQuery';
import { DealMatchingDataUpdateResponse, SelectedTranche } from '../../shared/models';
import { EnumUtils, MatchingUtils, NumberUtils } from '../../shared/utils';
import { MatchingSearch } from './MatchingSearch';
import { useMutation, useQueryClient } from 'react-query';
import { useApi } from '../../shared/context';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { useSnackbar } from 'notistack';
import { getApiResponseData } from '../../shared/utils/axios';
import { LenderMatchTableRow } from './LenderMatchTableRow';

export const MatchingTranches: React.FC = () => {
	const api = useApi();
	const theme = useTheme();
	const snackbar = useSnackbar();
	const queryClient = useQueryClient();
	const [tab, setTab] = React.useState('selected');
	const [trancheSelected, setTrancheSelected] = React.useState<SelectedTranche | null>(null);
	const [updatingMatching, setUpdatingMatching] = React.useState<boolean>(false);
	const [drillInFilter, setDrillInFilter] = React.useState<number>(0);
	const [searchMode, setSearchMode] = React.useState<number>(1);

	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};

	const { guid } = useParams<'guid'>();

	const dealQuery = useDealQuery(guid);
	const allocationQuery = useDealRequestAllocationQuery(guid);
	const matchingQuery = useMatchingQuery(guid);
	useIsMatchingProcessingQuery(guid, {
		enabled: dealQuery.isSuccess && dealQuery.data.isPendingMatchingUpdate,
		retry: true,
		retryDelay: 3000,
		onSuccess(data) {
			queryClient.invalidateQueries(QueryKeys.Deal.Root(guid));
			queryClient.invalidateQueries(QueryKeys.Matching.Get(guid));
		},
	});

	const lendersQuery = useMatchingTrancheLendersQuery(
		guid,
		trancheSelected?.mode,
		trancheSelected?.tier1,
		null,
		trancheSelected?.finderFee,
		drillInFilter,
		searchMode,
		{ enabled: trancheSelected != null }
	);

	const matchingUpdate = useMutation(
		async () => {
			const { data, error } = await getApiResponseData<DealMatchingDataUpdateResponse>(
				api.post(CerebroApiEndpoints.Matching.Get(guid))
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QueryKeys.Deal.Root(guid));
				queryClient.invalidateQueries(QueryKeys.Matching.Get(guid));
				snackbar.enqueueSnackbar('Matching data updated.', { variant: 'success' });
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: () => {
				setUpdatingMatching(false);
			},
		}
	);

	const onUpdateMatching = () => {
		setUpdatingMatching(true);
		matchingUpdate.mutate();
	};

	const onLoadLenders = (mode: number, tier1: number, finderFee: boolean, structure?: number | null) => {
		setTrancheSelected({ mode, tier1, finderFee, structure });
	};

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Matching</CerebroBreadcrumb>

			{dealQuery.data && dealQuery.data.isPendingMatchingUpdate == true && !dealQuery.data.isClosedDeal && (
				<Alert
					severity="info"
					sx={{
						marginBottom: 3,
						boxShadow: '2px 2px 2px 2px ' + theme.palette.primary.main,
						border: 'none',
						backgroundColor: 'white',
						fontWeight: 'bold',
					}}
				>
					Matching is currently being updated.
				</Alert>
			)}

			{dealQuery.data && dealQuery.data.matchingFailedUpdated == true && !dealQuery.data.isClosedDeal && (
				<Alert
					severity="error"
					sx={{
						marginBottom: 3,
						boxShadow: '2px 2px 2px 2px ' + theme.palette.primary.main,
						border: 'none',
						backgroundColor: 'white',
						fontWeight: 'bold',
					}}
				>
					Something went wrong while updating the matching data. Please update matching again.
				</Alert>
			)}

			{dealQuery.data?.showMatchingBlankIssue && (
				<Alert
					severity="info"
					sx={{
						marginBottom: 3,
						boxShadow: '2px 2px 2px 2px ' + theme.palette.primary.main,
						border: 'none',
						backgroundColor: 'white',
						fontWeight: 'bold',
					}}
				>
					This deal is closed with no matching data available. To see matching data the deal would need to be
					re-opened and matching updated.
				</Alert>
			)}

			<CerebroHeading size="h1" component="h1" sx={{ marginBottom: 3 }}>
				Lender Stats
			</CerebroHeading>

			<CerebroHeading size="h2" component="h2">
				Total Matches
			</CerebroHeading>

			<Table>
				<TableHead>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold' }}>Criteria</TableCell>
						<TableCell sx={{ fontWeight: 'bold' }}>Deal Value</TableCell>
						<TableCell sx={{ fontWeight: 'bold' }}>Bank Lenders</TableCell>
						<TableCell sx={{ fontWeight: 'bold' }}>Non-Bank Lenders</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow>
						<TableCell>Industry</TableCell>
						<TableCell>
							{matchingQuery.isSuccess ? matchingQuery.data.stats?.industryDisplayName : <Skeleton />}
						</TableCell>
						<TableCell>
							{/* Commercial Bank Count  */}
							<LenderCountLink
								mode={0}
								tier1={10}
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.industryBankMatches}
							/>
							<span> / </span>
							<LenderCountLink
								finderFees
								mode={0}
								tier1={10}
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.industryBankFinderFeeMatches}
							/>
						</TableCell>
						<TableCell>
							{/* Non-Bank Count */}
							<LenderCountLink
								mode={0}
								tier1={20}
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.industryNonBankMatches}
							/>
							<span> / </span>
							<LenderCountLink
								finderFees
								mode={0}
								tier1={20}
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.industryNonBankFinderFeeMatches}
							/>
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell>Geography</TableCell>
						<TableCell>
							{matchingQuery.isSuccess ? (
								matchingQuery.data.stats?.countryType &&
								matchingQuery.data.stats?.countryType != 'UnitedStates' ? (
									EnumUtils.GetCountryTypeDescription(matchingQuery.data.stats?.countryType)
								) : (
									matchingQuery.data.stats?.geographyDisplayName
								)
							) : (
								<Skeleton />
							)}
						</TableCell>
						<TableCell>
							{/* Commercial Bank Count  */}
							<LenderCountLink
								mode={1}
								tier1={10}
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.geographyBankMatches}
								title={`of the ${matchingQuery.data?.stats.industryBankMatches} Commercial Bank industry matches`}
							/>
							<span> / </span>
							<LenderCountLink
								mode={1}
								tier1={10}
								finderFees
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.geographyBankFinderFeeMatches}
								title={`of the ${matchingQuery.data?.stats.industryBankFinderFeeMatches} Commercial Bank finder fee industry matches`}
							/>
						</TableCell>
						<TableCell>
							{/* Non-Bank Count */}
							<LenderCountLink
								mode={1}
								tier1={20}
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.geographyNonBankMatches}
								title={`of the ${matchingQuery.data?.stats.industryNonBankMatches} Non-Bank industry matches`}
							/>
							<span> / </span>
							<LenderCountLink
								mode={1}
								tier1={20}
								finderFees
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.geographyNonBankFinderFeeMatches}
								title={`of the ${matchingQuery.data?.stats.industryNonBankFinderFeeMatches} Non-Bank finder fee industry matches`}
							/>
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell>
							<span title="Used to match to Commercial Banks">Revenue</span> /{' '}
							<span title="Used to match to Non-Bank Lenders">EBITDA</span>
						</TableCell>
						<TableCell>
							{matchingQuery.isSuccess ? (
								`${NumberUtils.FormatCurrency(
									matchingQuery.data.stats.revenue
								)} / ${NumberUtils.FormatCurrency(matchingQuery.data.stats.ebitda)}`
							) : (
								<Skeleton />
							)}
						</TableCell>

						<TableCell>
							{/* Commercial Bank Count  */}
							<LenderCountLink
								mode={5}
								tier1={10}
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.ebitdaRevenueBankMatches}
								title={`of the ${matchingQuery.data?.stats.geographyBankMatches} Commercial Bank geography matches`}
							/>
							<span> / </span>
							<LenderCountLink
								mode={5}
								tier1={10}
								finderFees
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.ebitdaRevenueBankFinderFeeMatches}
								title={`of the ${matchingQuery.data?.stats.geographyBankFinderFeeMatches} Commercial Bank geography matches`}
							/>
						</TableCell>

						<TableCell>
							{/* Non-Bank Count  */}
							<LenderCountLink
								mode={3}
								tier1={20}
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.ebitdaRevenueNonBankMatches}
								title={`of the ${matchingQuery.data?.stats.geographyNonBankMatches} Non-Bank geography matches`}
							/>
							<span> / </span>
							<LenderCountLink
								mode={3}
								tier1={20}
								finderFees
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.ebitdaRevenueNonBankFinderFeeMatches}
								title={`of the ${matchingQuery.data?.stats.geographyNonBankFinderFeeMatches} Non-Bank geography matches`}
							/>
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell>Deal Size</TableCell>
						<TableCell>
							{matchingQuery.isSuccess ? (
								NumberUtils.FormatCurrency(matchingQuery.data.stats.dealSize)
							) : (
								<Skeleton />
							)}
						</TableCell>
						<TableCell>
							{/* Commercial Bank Count  */}
							<LenderCountLink
								mode={5}
								tier1={10}
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.dealSizeBankMatches}
								title={`of the ${matchingQuery.data?.stats.ebitdaRevenueBankMatches} Commercial Bank revenue matches`}
							/>
							<span> / </span>
							<LenderCountLink
								mode={5}
								tier1={10}
								finderFees
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.dealSizeBankFinderFeeMatches}
								title={`of the ${matchingQuery.data?.stats.ebitdaRevenueBankFinderFeeMatches} Commercial Bank finder fee revenue matches`}
							/>
						</TableCell>
						<TableCell>
							{/* Non-Bank Count  */}
							<LenderCountLink
								mode={5}
								tier1={20}
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.dealSizeNonBankMatches}
								title={`of the ${matchingQuery.data?.stats.ebitdaRevenueNonBankMatches} Non-Bank EBITDA matches`}
							/>
							<span> / </span>
							<LenderCountLink
								mode={5}
								tier1={20}
								finderFees
								loadLenders={onLoadLenders}
								numMatches={matchingQuery.data?.stats.dealSizeNonBankFinderFeeMatches}
								title={`of the ${matchingQuery.data?.stats.ebitdaRevenueNonBankFinderFeeMatches} Non-Bank finder fee EBITDA matches`}
							/>
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>

			<TabContext value={tab}>
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<TabList onChange={handleTabChange}>
						<Tab label="Selected" value="selected" />
						<Tab label="Preferred" value="preferred" />
						<Tab label="Available" value="available" />
						<Tab label="Unavailable" value="unavailable" />
						<Tab label="Search" value="search" />
					</TabList>
				</Box>

				{(allocationQuery.isLoading || matchingQuery.isLoading) && <Skeleton height={300} />}

				{allocationQuery.isSuccess && matchingQuery.isSuccess && (
					<>
						<TabPanel value="selected">
							<MatchingGrid
								dealGuid={guid}
								deal={dealQuery.data}
								allocation={allocationQuery.data}
								stats={matchingQuery.data.stats}
								params={matchingQuery.data.params}
								tranches={matchingQuery.data.evaluations?.filter(
									tranche =>
										MatchingUtils.trancheSelectedByCerebro(
											tranche,
											matchingQuery.data.loanOptions
										) && tranche.showEvaluation
								)}
								tab="selected"
								isFetching={matchingQuery.isFetching}
							/>
						</TabPanel>
						<TabPanel value="preferred">
							<MatchingGrid
								dealGuid={guid}
								deal={dealQuery.data}
								allocation={allocationQuery.data}
								stats={matchingQuery.data.stats}
								params={matchingQuery.data.params}
								tranches={matchingQuery.data.evaluations?.filter(
									tranche =>
										MatchingUtils.trancheSelectedByBorrower(
											tranche,
											matchingQuery.data.loanOptions
										) && tranche.showEvaluation
								)}
								tab="preferred"
								isFetching={matchingQuery.isFetching}
							/>
						</TabPanel>
						<TabPanel value="available">
							<MatchingGrid
								dealGuid={guid}
								deal={dealQuery.data}
								allocation={allocationQuery.data}
								stats={matchingQuery.data.stats}
								params={matchingQuery.data.params}
								tranches={matchingQuery.data.evaluations?.filter(
									tranche => tranche.hasValue && tranche.showEvaluation
								)}
								tab="available"
								isFetching={matchingQuery.isFetching}
							/>
						</TabPanel>
						<TabPanel value="unavailable">
							<MatchingGrid
								dealGuid={guid}
								deal={dealQuery.data}
								allocation={allocationQuery.data}
								stats={matchingQuery.data.stats}
								params={matchingQuery.data.params}
								tranches={matchingQuery.data.evaluations?.filter(
									tranche => !tranche.hasValue && tranche.showEvaluation
								)}
								tab="none"
								isFetching={matchingQuery.isFetching}
							/>
						</TabPanel>
						<TabPanel value="search">
							<MatchingSearch dealGuid={allocationQuery.data.dealGuid} deal={dealQuery.data} />
						</TabPanel>
					</>
				)}
			</TabContext>

			<Grid container sx={{ marginTop: 2 }}>
				<Grid item xs={12} sx={{ textAlign: 'right' }}>
					<CerebroText variant="small">Last Updated: {dealQuery.data?.lastMatchingUpdateString}</CerebroText>
					{dealQuery.data && !dealQuery.data.isClosedDeal && (
						<>
							<br />
							<CerebroSecondaryButton
								loading={updatingMatching || dealQuery.data?.isPendingMatchingUpdate}
								onClick={() => onUpdateMatching()}
							>
								Update
							</CerebroSecondaryButton>
						</>
					)}
				</Grid>
			</Grid>

			{trancheSelected !== null && (
				<CerebroDrawer open onClose={() => setTrancheSelected(null)}>
					<Grid container spacing={2} sx={{ marginBottom: 3 }}>
						{(trancheSelected?.structure == 1 || trancheSelected?.structure == 2) && (
							<Grid item xs={12} sm={6} md={4}>
								<CerebroDropdown
									value={drillInFilter}
									onChange={data => setDrillInFilter(data.target.value as number)}
								>
									<CerebroDropdownMenuItem value={-1}>All Lenders</CerebroDropdownMenuItem>
									<CerebroDropdownMenuItem value={0}>Full Amount</CerebroDropdownMenuItem>
									<CerebroDropdownMenuItem value={1}>Partial Amount</CerebroDropdownMenuItem>
								</CerebroDropdown>
							</Grid>
						)}
						<Grid item xs={12} sm={6} md={4}>
							<CerebroDropdown
								value={searchMode}
								onChange={data => setSearchMode(data.target.value as number)}
							>
								<CerebroDropdownMenuItem value="1">Include All</CerebroDropdownMenuItem>
								<CerebroDropdownMenuItem value="2">Exclude Active</CerebroDropdownMenuItem>
								<CerebroDropdownMenuItem value="3">Exclude All Invited</CerebroDropdownMenuItem>
								<CerebroDropdownMenuItem value="4">Specialty Industry</CerebroDropdownMenuItem>
								<CerebroDropdownMenuItem value="5">Loan Watch Subscribers</CerebroDropdownMenuItem>
								<CerebroDropdownMenuItem value="6">New Lenders</CerebroDropdownMenuItem>
							</CerebroDropdown>
						</Grid>
					</Grid>

					<TableContainer sx={{ marginTop: 1 }}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell></TableCell>
									<TableCell>Lender Name</TableCell>
									<TableCell>Bank Name</TableCell>
									<TableCell>Lending Capacity</TableCell>
									<TableCell>Tier 3</TableCell>
									<TableCell>Criteria</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{lendersQuery.isLoading &&
									Array.apply(null, Array(10)).map((_, index) => (
										<TableRow key={index}>
											<TableCell colSpan={6}>
												<Skeleton />
											</TableCell>
										</TableRow>
									))}

								{lendersQuery.isSuccess &&
									lendersQuery.data.map(lender => {
										return (
											<LenderMatchTableRow
												key={lender.bankDealProfileGuid}
												lender={lender}
												isSubmitting={false}
												showCapacityColumn={true}
												allowSelection={false}
												isChecked={false}
												onChange={() => {}}
											/>
										);
									})}
							</TableBody>
						</Table>
					</TableContainer>
				</CerebroDrawer>
			)}
		</CerebroPageContainer>
	);
};

interface LenderCountLinkProps {
	mode: number;
	tier1: number;
	title?: string;
	numMatches?: number;
	finderFees?: boolean;

	loadLenders: (mode: number, tier1: number, finderFee: boolean, structure?: number | null) => void;
}

const LenderCountLink: React.FC<LenderCountLinkProps> = ({
	mode,
	tier1,
	title,
	numMatches,
	finderFees = false,
	loadLenders,
}) => {
	if (numMatches === undefined) {
		return (
			<CerebroText variant="small" component="span" title={title}>
				<Skeleton width={30} sx={{ display: 'inline-block' }} />
			</CerebroText>
		);
	}

	if (numMatches === 0)
		return (
			<CerebroText variant="small" component="span" title={title}>
				{numMatches}
			</CerebroText>
		);

	const onClick = () => {
		loadLenders(mode, tier1, finderFees, null);
	};

	return (
		<CerebroLink title={title} onClick={onClick} sx={{ cursor: 'pointer' }}>
			{numMatches}
		</CerebroLink>
	);
};
